import {
  ExternalUserFormSignatureInfo,
  FormAnswerSignatureState,
  PdfLink,
  SignatureBody,
  SignatureIndex,
  SignatureIndexParams,
  SignatureListFilters,
  SignatureRequestResponse,
  SignatureRequestTimelineResponse,
  SignatureResponse,
  SignatureStatusCounts,
  SignatureTabType,
  SignerInfo,
  UpdateExternalUserSignatureInfoBody,
  UserFormSignatureInfo,
  UserSignatureState,
} from '../types/signature';
import { client } from './index';

async function getSignature(type: string) {
  const response = await client.get<SignatureResponse>(
    `signatures/${type === 'user' ? 'mobile_user_config' : 'account_config'}`,
  );
  const { data } = response;
  return data;
}

async function updateAccountSignature(body: SignatureBody, userId: string) {
  const response = await client.post(`users/update_react/${userId}`, body);
  const { data } = response;
  return data;
}

async function getUserSignatureState() {
  const response = await client.get<UserSignatureState>('signatures/mobile_user/state');
  const { data } = response;
  return data;
}

async function getFormAnswerSignatureState(formAnswerId: string) {
  const response = await client.get<FormAnswerSignatureState>(
    `signatures/form_answer/${formAnswerId}`,
  );
  const { data } = response;
  return data;
}

async function getUserFormSignInfo(formAnswerId: string) {
  const response = await client.get<UserFormSignatureInfo>(
    `signatures/mobile_user/request/${formAnswerId}`,
  );
  const { data } = response;
  return data;
}

async function getExternalUserFormSignInfo(token: string) {
  const response = await client.get<ExternalUserFormSignatureInfo>(
    `signatures/get_external_user?token=${token}`,
  );
  const { data } = response;
  return data;
}

async function saveExternalUserSignatureInfo(
  token: string,
  body: UpdateExternalUserSignatureInfoBody,
) {
  const response = await client.post(`signatures/update_external_user?token=${token}`, body);
  const { data } = response;
  return data;
}

async function externalUserSignFormAnswer(token: string, formAnswerId: string) {
  const response = await client.put(
    `signatures/form_answer/${formAnswerId}/external_sign?token=${token}`,
  );
  const { data } = response;
  return data;
}

async function externalUserRequestNewLink(token: string) {
  const response = await client.post(
    `signatures/resend_individual_signature_request_email?token=${token}`,
  );
  const { data } = response;
  return data;
}

async function signFormAnswer(formAnswerId: string) {
  const response = await client.put(`signatures/form_answer/${formAnswerId}/sign`);
  const { data } = response;
  return data;
}

async function getSignatureIndex(params: SignatureIndexParams) {
  const {
    statuses,
    form_ids,
    requester_ids,
    participant_ids,
    location_with_descendants_id,
    ...rest
  } = params;
  const response = await client.get<SignatureIndex>('signatures/signatures_index', {
    // Arrays go as comma-joined scalars, matching what the counts endpoints and
    // the requests list already send; axios would otherwise serialise them as
    // repeated `key[]=` params, which the backend does not read.
    params: {
      ...rest,
      ...(statuses?.length ? { statuses: statuses.join(',') } : {}),
      ...(form_ids?.length ? { form_ids: form_ids.join(',') } : {}),
      ...(requester_ids?.length ? { requester_ids: requester_ids.join(',') } : {}),
      ...(participant_ids?.length ? { participant_ids: participant_ids.join(',') } : {}),
      ...(location_with_descendants_id?.length
        ? { location_with_descendants_id: location_with_descendants_id.join(',') }
        : {}),
    },
  });
  const { data } = response;
  return data;
}

// DS-3672. Counts behind the three status cards, one call per tab. Both are
// gated on SignatureRejectionUX server-side and 404 when it is off, so callers
// must tolerate the failure rather than surfacing it as a broken page.
// Filters go to the counts endpoints too, not just the lists: a card must count
// exactly what its own table shows. The backend enforces the same pairing.
function signatureFilterParams(search?: string, filters?: SignatureListFilters) {
  return {
    ...(search ? { search } : {}),
    ...(filters?.date_from ? { date_from: filters.date_from } : {}),
    ...(filters?.date_to ? { date_to: filters.date_to } : {}),
    // Travels with the range on every path, counts included. Left out of this
    // whitelist it made the cards read the sent date while the table read the
    // signature date, so a card counted rows its own list hid.
    ...(filters?.date_field ? { date_field: filters.date_field } : {}),
    ...(filters?.form_ids?.length ? { form_ids: filters.form_ids.join(',') } : {}),
    ...(filters?.form_code ? { form_code: filters.form_code } : {}),
    ...(filters?.requester_ids?.length ? { requester_ids: filters.requester_ids.join(',') } : {}),
    ...(filters?.participant_ids?.length
      ? { participant_ids: filters.participant_ids.join(',') }
      : {}),
    ...(filters?.statuses?.length ? { statuses: filters.statuses.join(',') } : {}),
    ...(filters?.location_with_descendants_id?.length
      ? { location_with_descendants_id: filters.location_with_descendants_id.join(',') }
      : {}),
  };
}

async function getSignaturesCounts(search?: string, filters?: SignatureListFilters) {
  const response = await client.get<SignatureStatusCounts>('signatures/signatures_counts', {
    params: signatureFilterParams(search, filters),
  });
  return response.data;
}

async function getSignatureRequestsCounts(search?: string, filters?: SignatureListFilters) {
  const response = await client.get<SignatureStatusCounts>('signatures/requests_counts', {
    params: signatureFilterParams(search, filters),
  });
  return response.data;
}

async function getSignatureRequests(
  search?: string,
  page?: number,
  filters?: SignatureListFilters,
  /**
   * Legacy single-status param, still sent when SignatureRejectionUX is off so
   * the rollback path hits the backend exactly as it does today. With the flag on
   * the status set travels in `filters.statuses` instead.
   */
  status?: SignatureTabType,
  perPage?: number,
) {
  const params = new URLSearchParams();

  if (search) params.append('search', search);
  if (page) params.append('page', page.toString());
  if (status) params.append('status', status);
  if (perPage) params.append('per_page', perPage.toString());
  Object.entries(signatureFilterParams(undefined, filters)).forEach(([key, value]) => {
    params.append(key, String(value));
  });

  const queryString = params.toString() ? `?${params.toString()}` : '';
  const url = `signatures/requests_index${queryString}`;

  const response = await client.get<SignatureRequestResponse>(url);
  const { data } = response;
  return data;
}

async function shareSignedPdf(requestId: number, emails: string[]) {
  const response = await client.post(`signatures/${requestId}/share_email`, { emails });
  const { data } = response;
  return data;
}

async function deleteSignatureRequest(requestId: number) {
  const response = await client.delete(`signatures/requests/${requestId}`);
  const { data } = response;
  return data;
}

async function getSignedPdfUrl(requestId: number) {
  const response = await client.get<PdfLink>(`signatures/${requestId}/pdf_link`);
  const { data } = response;
  return data;
}

async function bulkSign(requestIds: number[]) {
  const response = await client.post('signatures/requests/bulk_sign', { requests_ids: requestIds });
  const { data } = response;
  return data;
}

async function bulkSignReject(
  requestIds: number[],
  rejectReason?: string,
  rejectReasonId?: number,
): Promise<void> {
  await client.patch(
    'signatures/requests/bulk_reject',
    { request_ids: requestIds, reject_reason: rejectReason, reject_reason_id: rejectReasonId },
    {
      headers: {
        'Content-Type': 'application/json',
      },
    },
  );
}

async function externalSignReject(
  formAnswerId: number,
  token: string,
  requestId?: number,
  rejectReason?: string,
  rejectReasonId?: number,
) {
  const response = await client.patch(`signatures/requests/external_reject?token=${token}`, {
    form_answer_id: formAnswerId,
    rejects: { [requestId]: 0 },
    ...(rejectReasonId != null && { reject_reason_id: rejectReasonId }),
    ...(rejectReason != null && rejectReason.trim() !== '' && { reject_reason: rejectReason.trim() }),
  });
  const { data } = response;
  return data;
}

async function sendSlackNotification() {
  const response = await client.post('signatures/notify_sales');
  const { data } = response;
  return data;
}

// entryPoint tags where the correction was started from (the table vs the
// rejection email) for the section 8 metrics. The backend records anything it
// does not recognise as "unknown" rather than dropping the event (DS-3671/U6).
async function regenerateSignatureRequest(formAnswerId: string, entryPoint?: string) {
  const response = await client.post(
    `signatures/form_answer/${formAnswerId}/regenerate`,
    {},
    { params: entryPoint ? { entry_point: entryPoint } : {} },
  );
  const { data } = response;
  return data;
}

async function getSignatureRequestTimeline(requestId: number) {
  const response = await client.get<SignatureRequestTimelineResponse>(
    `signatures/requests/${requestId}/timeline`,
  );
  const { data } = response;
  return data;
}

async function getFormAnswerCompletedSignatures(formAnswerId: string) {
  const response = await client.get<SignerInfo[]>(
    `signatures/form_answer/${formAnswerId}/completed`,
  );
  const { data } = response;
  return data;
}

async function getPendingSignaturesCount() {
  const response = await client.get<{ pending_signatures: number }>(
    'signatures/mobile_user/pending_signatures',
  );
  const { data } = response;
  return data;
}

async function getIfImplementationWasRequested() {
  const response = await client.get('signatures/check_if_implementation_was_requested');
  const { data } = response;
  return data.requested;
}

async function requestImplementation() {
  const response = await client.put('signatures/request_implementation');
  const { data } = response;
  return data;
}

async function getImplementationRequestDate() {
  const response = await client.get('signatures/check_implementation_request_date');
  const { data } = response;
  return data;
}

async function notifySignatureRequestEmail(requestId: number) {
  const response = await client.post(`signatures/notify_signature_request_email`, {
    id: requestId,
  });
  const { data } = response;
  return data;
}

export const signatureApi = {
  getSignature,
  updateAccountSignature,
  getUserSignatureState,
  getFormAnswerSignatureState,
  getUserFormSignInfo,
  getExternalUserFormSignInfo,
  saveExternalUserSignatureInfo,
  externalUserSignFormAnswer,
  externalUserRequestNewLink,
  signFormAnswer,
  getSignatureRequests,
  getSignatureIndex,
  getSignaturesCounts,
  getSignatureRequestsCounts,
  shareSignedPdf,
  bulkSign,
  bulkSignReject,
  getSignedPdfUrl,
  deleteSignatureRequest,
  sendSlackNotification,
  getSignatureRequestTimeline,
  externalSignReject,
  regenerateSignatureRequest,
  getFormAnswerCompletedSignatures,
  getPendingSignaturesCount,
  getIfImplementationWasRequested,
  requestImplementation,
  getImplementationRequestDate,
  notifySignatureRequestEmail,
};
